// src/pages/Admin/Shipments.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  LocalShipping,
  CheckCircle,
  Schedule,
  Warning,
  Download,
  PictureAsPdf,
  LocationOn,
  CalendarToday,
  AccessTime,
  ExpandMore,
  Add,
  Timeline,
  Map,
  FileCopy,
  Chat,
  Email,
  Phone,
  Cached,
  DoneAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatDate, formatDateTime, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminShipments = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [containerTypeFilter, setContainerTypeFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [updateDialog, setUpdateDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedShipment, setExpandedShipment] = useState(null);
  const [newShipment, setNewShipment] = useState({
    enquiryId: '',
    containerNumber: '',
    containerType: '40ft',
    vesselName: '',
    portOfLoading: '',
    portOfDischarge: '',
    expectedDeparture: '',
    expectedArrival: '',
  });

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllShipments();
      setShipments(response.data);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statusMap = ['all', 'pending', 'assigned', 'loading', 'in_transit', 'arrived', 'delivered'];
    setStatusFilter(statusMap[newValue]);
  };

  const handleCreateShipment = async () => {
    try {
      await adminAPI.createShipment(newShipment);
      toast.success('Shipment created successfully');
      fetchShipments();
      setCreateDialog(false);
      setNewShipment({
        enquiryId: '',
        containerNumber: '',
        containerType: '40ft',
        vesselName: '',
        portOfLoading: '',
        portOfDischarge: '',
        expectedDeparture: '',
        expectedArrival: '',
      });
    } catch (error) {
      console.error('Error creating shipment:', error);
      toast.error('Failed to create shipment');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await adminAPI.updateShipmentStatus(id, { status });
      toast.success(`Shipment status updated to ${status}`);
      fetchShipments();
      setUpdateDialog(null);
    } catch (error) {
      console.error('Error updating shipment status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleAssignContainer = async (id, containerNumber) => {
    try {
      await adminAPI.updateShipment(id, { containerNumber, status: 'assigned' });
      toast.success('Container assigned successfully');
      fetchShipments();
      setAssignDialog(null);
    } catch (error) {
      console.error('Error assigning container:', error);
      toast.error('Failed to assign container');
    }
  };

  const generateBLNumber = () => {
    return `BL${Date.now().toString().slice(-8)}`;
  };

  const stats = {
    total: shipments.length,
    pending: shipments.filter(s => s.status === 'pending').length,
    inTransit: shipments.filter(s => s.status === 'in_transit').length,
    delivered: shipments.filter(s => s.status === 'delivered').length,
    delayed: shipments.filter(s => s.status === 'delayed').length,
  };

  const filteredShipments = shipments.filter(shipment => {
    const matchesSearch = 
      shipment.containerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.enquiryId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.blNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;
    const matchesType = containerTypeFilter === 'all' || shipment.containerType === containerTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getShippingSteps = (status) => {
    const steps = [
      { label: 'Order Confirmed', status: 'confirmed' },
      { label: 'Container Assigned', status: 'assigned' },
      { label: 'Port Loading', status: 'loading' },
      { label: 'In Transit', status: 'in_transit' },
      { label: 'Port Arrival', status: 'arrived' },
      { label: 'Delivered', status: 'delivered' },
    ];
    
    const statusOrder = ['confirmed', 'assigned', 'loading', 'in_transit', 'arrived', 'delivered'];
    const currentIndex = statusOrder.indexOf(status);
    
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      active: index === currentIndex + 1,
    }));
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading shipments...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Shipments Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage all shipments and containers
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchShipments}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialog(true)}
          >
            Create Shipment
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Shipments
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#2196f315', color: '#2196f3' }}>
                  <LocalShipping />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    In Transit
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.inTransit}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#00bcd415', color: '#00bcd4' }}>
                  <Timeline />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Delivered
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.delivered}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Delayed
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.delayed}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#ff980015', color: '#ff9800' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" icon={<LocalShipping />} iconPosition="start" />
          <Tab label="Pending" icon={<Schedule />} iconPosition="start" />
          <Tab label="Assigned" icon={<CheckCircle />} iconPosition="start" />
          <Tab label="In Transit" icon={<Timeline />} iconPosition="start" />
          <Tab label="Arrived" icon={<LocationOn />} iconPosition="start" />
          <Tab label="Delivered" icon={<DoneAll />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by container, enquiry ID, vessel, or BL number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Container Type</InputLabel>
              <Select
                label="Container Type"
                value={containerTypeFilter}
                onChange={(e) => setContainerTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="20ft">20ft Container</MenuItem>
                <MenuItem value="40ft">40ft Container</MenuItem>
                <MenuItem value="40ft_reefer">40ft Reefer</MenuItem>
                <MenuItem value="45ft">45ft Container</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Departure Port</InputLabel>
              <Select label="Departure Port" defaultValue="all">
                <MenuItem value="all">All Ports</MenuItem>
                <MenuItem value="shanghai">Shanghai</MenuItem>
                <MenuItem value="rotterdam">Rotterdam</MenuItem>
                <MenuItem value="singapore">Singapore</MenuItem>
                <MenuItem value="busan">Busan</MenuItem>
                <MenuItem value="ningbo">Ningbo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              startIcon={<FilterList />}
              variant="outlined"
              onClick={() => {}}
            >
              More Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Shipments Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Container</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Vessel</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Ports</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">ETA</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <LocalShipping sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No shipments found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredShipments.map((shipment) => (
                  <React.Fragment key={shipment._id}>
                    <TableRow hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {shipment.containerNumber || 'Not assigned'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type: {shipment.containerType}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Enquiry: #{shipment.enquiryId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {shipment.vesselName || 'Not assigned'}
                          </Typography>
                          {shipment.voyageNumber && (
                            <Typography variant="caption" color="text.secondary">
                              Voyage: {shipment.voyageNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption" display="block" color="text.secondary">
                            From: {shipment.portOfLoading}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            To: {shipment.portOfDischarge}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            label={shipment.status.replace('_', ' ')}
                            size="small"
                            color={getStatusColor(shipment.status)}
                            sx={{ fontWeight: 'medium', mb: 0.5 }}
                          />
                          {shipment.blNumber && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              BL: {shipment.blNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(shipment.expectedArrival)}
                        </Typography>
                        {shipment.actualArrival && (
                          <Typography variant="caption" color="success.main">
                            Arrived: {formatDate(shipment.actualArrival)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!shipment.containerNumber && (
                            <Tooltip title="Assign Container">
                              <IconButton
                                size="small"
                                onClick={() => setAssignDialog(shipment)}
                                color="primary"
                              >
                                <LocalShipping />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Update Status">
                            <IconButton
                              size="small"
                              onClick={() => setUpdateDialog(shipment)}
                              color="secondary"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setExpandedShipment(expandedShipment === shipment._id ? null : shipment._id)}
                            >
                              <ExpandMore 
                                sx={{ 
                                  transform: expandedShipment === shipment._id ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.2s'
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    {expandedShipment === shipment._id && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: 'grey.50', p: 3 }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Shipping Progress
                                </Typography>
                                <Stepper alternativeLabel sx={{ mb: 3 }}>
                                  {getShippingSteps(shipment.status).map((step) => (
                                    <Step key={step.label} completed={step.completed} active={step.active}>
                                      <StepLabel>{step.label}</StepLabel>
                                    </Step>
                                  ))}
                                </Stepper>
                                
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary">
                                      Expected Departure
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                      {formatDate(shipment.expectedDeparture)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary">
                                      Expected Arrival
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                      {formatDate(shipment.expectedArrival)}
                                    </Typography>
                                  </Grid>
                                  {shipment.actualDeparture && (
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="textSecondary">
                                        Actual Departure
                                      </Typography>
                                      <Typography variant="body2" fontWeight="medium" color="success.main">
                                        {formatDateTime(shipment.actualDeparture)}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {shipment.actualArrival && (
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="textSecondary">
                                        Actual Arrival
                                      </Typography>
                                      <Typography variant="body2" fontWeight="medium" color="success.main">
                                        {formatDateTime(shipment.actualArrival)}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Quick Actions
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<FileCopy />}
                                    onClick={() => {
                                      if (shipment.blNumber) {
                                        navigator.clipboard.writeText(shipment.blNumber);
                                        toast.success('BL Number copied');
                                      }
                                    }}
                                    disabled={!shipment.blNumber}
                                  >
                                    Copy BL Number
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<PictureAsPdf />}
                                    onClick={() => toast.success('Generating BL document...')}
                                    disabled={!shipment.blNumber}
                                  >
                                    Generate BL
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<Chat />}
                                    onClick={() => navigate(`/chat/${shipment.enquiryId}`)}
                                  >
                                    Contact Buyer
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<Map />}
                                    href={shipment.trackingUrl}
                                    target="_blank"
                                    disabled={!shipment.trackingUrl}
                                  >
                                    Track Vessel
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>

                            <Grid item xs={12}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Additional Information
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">
                                      Shipping Line
                                    </Typography>
                                    <Typography variant="body2">
                                      {shipment.shippingLine || 'Maersk Line'}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">
                                      Temperature
                                    </Typography>
                                    <Typography variant="body2">
                                      {shipment.temperature || '2-4°C'}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">
                                      Cargo Weight
                                    </Typography>
                                    <Typography variant="body2">
                                      {shipment.cargoWeight || '25,000 kg'}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">
                                      Insurance
                                    </Typography>
                                    <Typography variant="body2">
                                      {shipment.insurance || 'All Risk'}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Shipment Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Shipment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Enquiry ID"
                value={newShipment.enquiryId}
                onChange={(e) => setNewShipment({...newShipment, enquiryId: e.target.value})}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Container Number"
                value={newShipment.containerNumber}
                onChange={(e) => setNewShipment({...newShipment, containerNumber: e.target.value})}
                size="small"
                placeholder="e.g., MSCU1234567"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Container Type</InputLabel>
                <Select
                  label="Container Type"
                  value={newShipment.containerType}
                  onChange={(e) => setNewShipment({...newShipment, containerType: e.target.value})}
                >
                  <MenuItem value="20ft">20ft Container</MenuItem>
                  <MenuItem value="40ft">40ft Container</MenuItem>
                  <MenuItem value="40ft_reefer">40ft Reefer</MenuItem>
                  <MenuItem value="45ft">45ft Container</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vessel Name"
                value={newShipment.vesselName}
                onChange={(e) => setNewShipment({...newShipment, vesselName: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Port of Loading"
                value={newShipment.portOfLoading}
                onChange={(e) => setNewShipment({...newShipment, portOfLoading: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Port of Discharge"
                value={newShipment.portOfDischarge}
                onChange={(e) => setNewShipment({...newShipment, portOfDischarge: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Departure"
                type="date"
                value={newShipment.expectedDeparture}
                onChange={(e) => setNewShipment({...newShipment, expectedDeparture: e.target.value})}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Arrival"
                type="date"
                value={newShipment.expectedArrival}
                onChange={(e) => setNewShipment({...newShipment, expectedArrival: e.target.value})}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateShipment}
            variant="contained"
          >
            Create Shipment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog
        open={Boolean(updateDialog)}
        onClose={() => setUpdateDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Shipment Status</DialogTitle>
        <DialogContent>
          {updateDialog && (
            <>
              <DialogContentText gutterBottom>
                Update status for Container: <strong>{updateDialog.containerNumber}</strong>
              </DialogContentText>
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    defaultValue={updateDialog.status}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="assigned">Assigned</MenuItem>
                    <MenuItem value="loading">Loading</MenuItem>
                    <MenuItem value="in_transit">In Transit</MenuItem>
                    <MenuItem value="arrived">Arrived</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="delayed">Delayed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {updateDialog.status === 'in_transit' && (
                <TextField
                  fullWidth
                  label="Tracking URL"
                  type="url"
                  placeholder="https://www.marinetraffic.com/..."
                  sx={{ mt: 2 }}
                  size="small"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleUpdateStatus(updateDialog?._id, 'in_transit')}
            variant="contained"
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Container Dialog */}
      <Dialog
        open={Boolean(assignDialog)}
        onClose={() => setAssignDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Container</DialogTitle>
        <DialogContent>
          {assignDialog && (
            <>
              <DialogContentText gutterBottom>
                Assign container to Enquiry: <strong>#{assignDialog.enquiryId}</strong>
              </DialogContentText>
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Container Number"
                  placeholder="e.g., MSCU1234567"
                  size="small"
                />
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    Container number must follow ISO standard format.
                    BL number will be automatically generated.
                  </Alert>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleAssignContainer(assignDialog?._id, 'MSCU1234567')}
            variant="contained"
          >
            Assign Container
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminShipments;